import type {
  LoginBarStyleValues,
  LoginSocialBarData,
  StyleProperties,
  CarmiCss,
  LoginSocialBarProperties,
  Direction,
} from '../../LoginSocialBar.types';
import { ArrowShape, Layout } from '../../LoginSocialBar.types';
import { appendCSSUnit, getLoginBarStyleValues } from '../../utils';
import { flexAlignMap } from './constants';
import { defaultCompProps } from '../../constants';
import { getFixedCSSVars } from './utils';

type GetCssMapperOutputOptions = {
  compData: LoginSocialBarData;
  compProps: LoginSocialBarProperties;
  styleProperties: StyleProperties;
  carmiCss: CarmiCss;
};

const getDisplayModeCSSVars = ({
  loggedInMember,
  loggedOutMember,
}: LoginBarStyleValues) => ({
  '--logged-in-avatar-display':
    loggedInMember === Layout.TextOnly ? 'none' : 'block',

  '--logged-in-text-display':
    loggedInMember === Layout.AvatarOnly ? 'none' : 'block',

  '--logged-out-avatar-display':
    loggedOutMember === Layout.TextOnly ? 'none' : 'block',

  '--logged-out-text-display':
    loggedOutMember === Layout.AvatarOnly ? 'none' : 'block',
});

const getArrowCSSVars = ({ arrowShape, arrowSize }: LoginBarStyleValues) => {
  return {
    '--arrow-display': arrowShape === ArrowShape.None ? 'none' : 'block',

    '--arrow-icon':
      arrowShape === ArrowShape.Line
        ? 'var(--line-arrow-icon)'
        : 'var(--full-arrow-icon)',

    '--arrow-size': appendCSSUnit(arrowSize),
  };
};

const getAvatarCSSVars = ({
  iconSize,
  loggedOutIconSize,
}: LoginBarStyleValues) => ({
  '--icon-size': appendCSSUnit(iconSize),
  '--logged-out-icon-size': appendCSSUnit(loggedOutIconSize),
});

const getButtonTextCSSVars = (showGreetingText: boolean) => ({
  '--greeting-text-display': showGreetingText ? 'contents' : 'none',
});

// TODO: https://wix.atlassian.net/browse/MA-4509
const getIconsDisplayCSSVars = (showBellIcon: boolean) => ({
  '--icons-display': showBellIcon ? 'block' : 'none',
  '--user-section-padding-start': showBellIcon ? '0px' : 'unset',
});

const getAlignmentCSSVars = (
  { buttonsAlignment, dropDownTextAlignment }: LoginBarStyleValues,
  compProps: LoginSocialBarProperties,
) => {
  const buttonsDirection = (compProps.buttonsDirection ??
    defaultCompProps.buttonsDirection) as Direction;

  const alignMap = flexAlignMap[buttonsDirection];

  const justifyContent = alignMap[buttonsAlignment];
  const menuItemsAlignment = alignMap[dropDownTextAlignment];

  return {
    'justify-content': justifyContent,
    '--menu-items-alignment': menuItemsAlignment,
  };
};

export const getCssMapperOutput = ({
  compData,
  compProps,
  styleProperties,
  carmiCss,
}: GetCssMapperOutputOptions) => {
  const styleValues = getLoginBarStyleValues({
    compData,
    compProps,
    styleProperties,
  });

  return {
    ...carmiCss,
    ...getDisplayModeCSSVars(styleValues),
    ...getAvatarCSSVars(styleValues),
    ...getArrowCSSVars(styleValues),
    ...getButtonTextCSSVars(styleValues.showLoggedInText),
    ...getIconsDisplayCSSVars(styleValues.showBellIcon),
    ...getAlignmentCSSVars(styleValues, compProps),
    ...getFixedCSSVars(styleProperties),
  };
};
